<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {computed} from "vue";
import {useStore} from "vuex";
import {filterOutActionFromQueryDataById} from "@/composables/useActions";
import {errorToast, infoToast, successToast} from "@/composables/useToasts";

const inputValue = defineModel({
  type: [String],
  required: true,
});

const props = defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['showError']);

const queryClient = useQueryClient();
const store = useStore();

const activeTab = computed(() => store.getters['actions/activeTab']);
const buttonText = computed(() => props.action.link_text ? props.action.link_text : 'Complete Action');
const actionRequiresInputToComplete = computed(() => props.action.input_label);

const optimisticallyMoveAction = async (actionId) => {
  await queryClient.cancelQueries({queryKey: ['hidden-actions']});
  await queryClient.cancelQueries({queryKey: ['actions']});
  await queryClient.cancelQueries({queryKey: ['action-progress']});
  await queryClient.cancelQueries({queryKey: ['action-categories']});

  queryClient.setQueryData(['action-progress'], (old) => {
    if (!old) {
      return [];
    }

    return {
      ...old,
      lastCompletedActionTitle: props.action.title,
    }
  });

  return filterOutActionFromQueryDataById(activeTab, queryClient, actionId);
}

const {mutate: completeAction} = useMutation({
  mutationFn: async (actionId) => await axios.post(`/ajax/v2/actions/${actionId}/complete`, {
    input: inputValue.value
  }),
  onMutate: async (actionId) => {
    if (actionRequiresInputToComplete.value && !inputValue.value) {
      emit('showError');
      throw new Error('');
    }

    if (actionRequiresInputToComplete.value) { // don't optimistically move actions requiring input, wait BE to respond
      return;
    }

    const previousActions = await optimisticallyMoveAction(actionId);

    infoToast('We’re currently crawling your website.  This action will be listed as complete once the crawl has finished!', 'Pending');

    return {previousActions}
  },
  onError: (error, _, context) => {
    const {message} = error.response.data;

    if (error.response.status === 422) {
      emit('showError', message);
    } else {
      errorToast(message ?? 'Something went wrong completing action');
    }

    queryClient.setQueryData(['actions'], context.previousActions);
  },
  onSuccess: async(_, actionId) => {
    if (!actionRequiresInputToComplete.value) {
      return;
    }

    successToast('Action completed');

    window.mixpanel('Actions v2: Complete action', {
      action_id: actionId,
    })

   await optimisticallyMoveAction(actionId);
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['actions'] });
    queryClient.invalidateQueries({ queryKey: ['hidden-actions'] });
    queryClient.invalidateQueries({ queryKey: ['action-progress'] });
    queryClient.invalidateQueries({ queryKey: ['action-categories'] });
  }
});
</script>

<template>
  <DefaultButton
    :as="action.link ? 'a' : 'button'"
    class="tw-bg-green tw-text-white tw-flex tw-items-center tw-gap-2"
    variant="everest"
    size="small"
    :href="action.link || undefined"
    @click="!action.link ? completeAction(action.id) : null"
  >
    <svg-vue
      icon="icons/tick-ultra-thin"
      class="tw-w-6"
    />

    <span class="tw-font-bold tw-text-sm">{{ buttonText }}</span>
  </DefaultButton>
</template>
